import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Rating } from "primereact/rating";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { FilterMatchMode } from "primereact/api";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import "./feedback1.css";

export default function Feedback1() {
  const [feedbacks, setFeedbacks] = useState([]);
  const [filters, setFilters] = useState({});
  const [globalFilter, setGlobalFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const toastRef = useRef(null);

  const [selectedComment, setSelectedComment] = useState("");
  const [showCommentModal, setShowCommentModal] = useState(false);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_BASE}/api/userFeedback/loadfeedback1`)
      .then((res) => {
        setFeedbacks(res.data);
        initFilters();
        setLoading(false);
      })
      .catch(() => {
        toastRef.current.show({
          severity: "error",
          summary: "Error",
          detail: "❌ Failed to load feedback data",
          life: 3000,
        });
        setLoading(false);
      });
  }, []);

  const initFilters = () => {
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      email: { value: null, matchMode: FilterMatchMode.CONTAINS },
      comment: { value: null, matchMode: FilterMatchMode.CONTAINS },
      overallRating: { value: null, matchMode: FilterMatchMode.EQUALS },
      foodRating: { value: null, matchMode: FilterMatchMode.EQUALS },
      serviceRating: { value: null, matchMode: FilterMatchMode.EQUALS },
      atmosphereRating: { value: null, matchMode: FilterMatchMode.EQUALS },
    });
  };

  const sendFeedback = () => {
    if (!email || !email.includes("@")) {
      toastRef.current.show({
        severity: "warn",
        summary: "Warning",
        detail: "⚠️ Please enter a valid email.",
        life: 3000,
      });
      return;
    }

    setSending(true);

    axios
      .post(`${process.env.REACT_APP_API_BASE}/api/userFeedback/send`, {
        email,
      })
      .then(() => {
        toastRef.current.show({
          severity: "success",
          summary: "Success",
          detail: "✅ Feedback email sent!",
          life: 3000,
        });
        setEmail(""); // Clear the input
        setSending(false);
      })
      .catch(() => {
        toastRef.current.show({
          severity: "error",
          summary: "Error",
          detail: "❌ Failed to send feedback email",
          life: 3000,
        });
        setSending(false);
      });
  };

  const getRatingColor = (rating) => {
    if (rating >= 5) return "rating-green";
    if (rating >= 3) return "rating-blue";
    return "rating-red";
  };

  const ratingBodyTemplate = (rating) => {
    const colorClass = getRatingColor(rating);
    return (
      <div className={colorClass}>
        <Rating value={rating} readOnly cancel={false} />
      </div>
    );
  };

  const ratingFilterTemplate = (options) => {
    const ratingOptions = [1, 2, 3, 4, 5].map((star) => ({
      label: `${star} Stars`,
      value: star,
    }));

    return (
      <Dropdown
        value={options.value}
        options={ratingOptions}
        onChange={(e) => options.filterApplyCallback(e.value)}
        placeholder="Select Stars"
        className="p-column-filter"
        showClear
        itemTemplate={(option) => (
          <div className="flex align-items-center">
            <Rating value={option.value} readOnly cancel={false} stars={5} />
          </div>
        )}
        optionLabel="label"
      />
    );
  };

  const commentBodyTemplate = (rowData) => {
    const comment = rowData.comment || "";
    const isLong = comment.length > 30;

    return (
      <div data-label="Comment" className="truncate-comment">
        {isLong ? (
          <>
            {comment.slice(0, 60)}...
            <Button
              label="More"
              link
              className="p-button-sm p-0 ml-2"
              onClick={() => {
                setSelectedComment(comment);
                setShowCommentModal(true);
              }}
            />
          </>
        ) : (
          comment
        )}
      </div>
    );
  };

  const tableHeader = (
    <div className="flex justify-content-end mb-3">
      <InputText
        value={globalFilter}
        onChange={(e) => {
          setGlobalFilter(e.target.value);
          setFilters((prev) => ({
            ...prev,
            global: {
              value: e.target.value,
              matchMode: FilterMatchMode.CONTAINS,
            },
          }));
        }}
        placeholder="Search feedback..."
        className="w-full md:w-25rem"
      />
    </div>
  );

  return (
    <div className="p-4 user-feedback-container">
      <Toast ref={toastRef} position="top-right" />
      <h2 className="feedback-title">Feedback 1</h2>

      <div className="feedback-input-row">
        <InputText
          placeholder="Enter Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="feedback-input"
        />
        <Button
          label={sending ? "Sending..." : "Send"}
          onClick={sendFeedback}
          className="send-button"
          disabled={sending}
        />
      </div>

      <div className="feedback-table-container">
        <DataTable
          value={feedbacks}
          paginator
          rows={10}
          loading={loading}
          filters={filters}
          globalFilterFields={[
            "email",
            "comment",
            "overallRating",
            "foodRating",
            "serviceRating",
            "atmosphereRating",
          ]}
          onFilter={(e) => setFilters(e.filters)}
          filterDisplay="row"
          header={tableHeader}
          emptyMessage="No feedbacks found."
          className="p-datatable-sm"
          responsiveLayout="scroll"
        >
          <Column
            field="email"
            header="Email"
            filter
            filterPlaceholder="Search by Email"
            showFilterMenu={false}
            body={(rowData) => <div data-label="Email">{rowData.email}</div>}
          />
          <Column
            field="overallRating"
            header="Overall Rating"
            body={(rowData) => (
              <div data-label="Overall Rating">
                {ratingBodyTemplate(rowData.overallRating)}
              </div>
            )}
            filter
            filterElement={ratingFilterTemplate}
            showFilterMenu={false}
          />
          <Column
            field="comment"
            header="Comment"
            filter
            filterPlaceholder="Search by Comment"
            showFilterMenu={false}
            body={commentBodyTemplate}
          />
          <Column
            field="foodRating"
            header="Food"
            body={(rowData) => (
              <div data-label="Food">
                {ratingBodyTemplate(rowData.foodRating)}
              </div>
            )}
            filter
            filterElement={ratingFilterTemplate}
            showFilterMenu={false}
          />
          <Column
            field="serviceRating"
            header="Service"
            body={(rowData) => (
              <div data-label="Service">
                {ratingBodyTemplate(rowData.serviceRating)}
              </div>
            )}
            filter
            filterElement={ratingFilterTemplate}
            showFilterMenu={false}
          />
          <Column
            field="atmosphereRating"
            header="Atmosphere"
            body={(rowData) => (
              <div data-label="Atmosphere">
                {ratingBodyTemplate(rowData.atmosphereRating)}
              </div>
            )}
            filter
            filterElement={ratingFilterTemplate}
            showFilterMenu={false}
          />
        </DataTable>
      </div>

      <Dialog
        header="Full Comment"
        visible={showCommentModal}
        onHide={() => setShowCommentModal(false)}
        style={{ width: "50vw" }}
        modal
      >
        <p style={{ whiteSpace: "pre-wrap" }}>{selectedComment}</p>
      </Dialog>
    </div>
  );
}
